// pronto.jsx — the "Glass" design system for the Pronto redesign.
//
// Source of truth: docs/pronto-redesign.md (§3 tokens, §8 component list) and the
// visual reference docs/design_handoff_pronto/Pronto.dc.html. Built ALONGSIDE the
// legacy ios.jsx / ui.jsx primitives so the live app keeps running until Phase 4
// migrates each screen over. Presentational only — no data-layer coupling.
//
// The system: frosted glass cards over an Apple system-neutral background, one
// warm gray-navy accent (#3F4A63), SF Pro Text for UI, SF Pro Rounded for
// numerals/headings, a New York serif accent for flourishes. Space and
// translucency do the work instead of borders.
import { ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

// Shared style atoms (kept as constants so every primitive reads the same token).
export const GLASS =
  "bg-[var(--pg)] border border-[var(--pg-border)] backdrop-blur-[20px]";
const SEP = "border-[var(--p-sep)]";
const ACCENT_GRAD = "bg-[linear-gradient(180deg,#55617E,#3F4A63)]";

/* ============================ Surfaces ============================ */

// The core Glass panel. `radius` maps to the spec's hierarchy (hero=24 … 14).
export function GlassCard({ children, className = "", radius = 20, elevated = false, ...rest }) {
  return (
    <div
      className={`${GLASS} ${elevated ? "shadow-pronto-hero" : "shadow-pronto-rest"} ${className}`}
      style={{ borderRadius: radius }}
      {...rest}
    >
      {children}
    </div>
  );
}

// Solid dark card (Plan "la spesa" summary, Listen briefing hero). White text.
export function SolidCard({ children, className = "", radius = 20, ...rest }) {
  return (
    <div
      className={`bg-pronto-solid text-white shadow-pronto-hero ${className}`}
      style={{ borderRadius: radius }}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ============================ Buttons ============================ */

// Loud CTA — accent gradient. Use sparingly (Suggerisci, Add all, new-chat +).
export function PrimaryButton({ children, onClick, disabled, type = "button", className = "" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`h-11 px-4 rounded-[14px] ${ACCENT_GRAD} text-white font-sf font-semibold text-[15px] shadow-pronto-btn flex items-center justify-center gap-2 disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

// Low-priority action — tonal accent fill, no shadow (e.g. "Push to Aiden").
export function TonalButton({ children, onClick, disabled, type = "button", className = "" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`h-11 px-4 rounded-[14px] bg-[rgba(63,74,99,0.12)] text-pronto-navy font-sf font-semibold text-[15px] flex items-center justify-center gap-2 disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

// Outline / secondary — translucent with an accent hairline (e.g. "Andiamo →").
export function SecondaryButton({ children, onClick, disabled, type = "button", className = "" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`h-11 px-4 rounded-[14px] bg-[var(--pg-4)] border border-[rgba(63,74,99,0.5)] text-pronto-navy font-sf font-semibold text-[15px] flex items-center justify-center gap-2 disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

/* ============================ Chips ============================ */

// Pill / filter chip. tone: "selected" (ink), "accent" (navy), "rest" (glass).
// `pressed` (optional): when the chip is a toggle, pass the boolean selected
// state so it exposes aria-pressed for assistive tech. Omit for link/action chips.
export function Chip({ children, tone = "rest", onClick, className = "", pressed, ...rest }) {
  const tones = {
    selected: "bg-pronto-solid text-white border border-transparent",
    accent: "bg-pronto-navy text-white border border-transparent",
    rest: "bg-[var(--pg-7)] text-pronto-ink border border-[var(--p-sep-12)]",
  };
  const Tag = onClick ? "button" : "span";
  return (
    <Tag
      onClick={onClick}
      aria-pressed={onClick && typeof pressed === "boolean" ? pressed : undefined}
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 font-sf text-[12.5px] font-semibold ${tones[tone]} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ============================ Segmented control ============================ */

// variant: "solid" (default — dark selected pill, harness style) | "pill" (Morning
// Mode branch + Hot/Cold: white selected pill with a soft shadow over the Apple
// segmented track rgba(118,118,128,0.1), navy text; idle transparent gray).
export function Segmented({ options, value, onChange, className = "", variant = "solid", radius = 11 }) {
  const pill = variant === "pill";
  return (
    <div
      className={`flex ${pill ? "bg-[rgba(118,118,128,0.1)] p-[3px] gap-[2px]" : "bg-[var(--pg)] p-0.5"} ${className}`}
      style={{ borderRadius: radius + 1 }}
    >
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            aria-pressed={on}
            style={{ borderRadius: radius - 2 }}
            className={`flex-1 py-1.5 font-sf text-[12.5px] transition-colors motion-reduce:transition-none ${
              pill
                ? on
                  ? "bg-white text-pronto-navy font-semibold shadow-[0_1px_3px_rgba(48,50,66,0.16)]"
                  : "text-pronto-text-2 font-medium"
                : on
                  ? "bg-pronto-solid text-white font-semibold"
                  : "text-pronto-text-2 font-semibold"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ============================ Spec tiles ============================ */

// Compact readout row (Coffee: Temp / Ratio / Bloom).
export function SpecTiles({ items, className = "" }) {
  return (
    <div
      className={`grid gap-2 ${className}`}
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0,1fr))` }}
    >
      {items.map((it, i) => (
        <div key={i} className="bg-[var(--pg-6)] rounded-[13px] py-2 px-1 text-center">
          <div className="font-sf text-[10px] font-semibold uppercase tracking-[0.05em] text-pronto-text-3">
            {it.label}
          </div>
          <div className="font-round font-semibold text-[16px] leading-tight text-pronto-ink tabular mt-0.5">
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================ Titles ============================ */

// iOS large-title header. Rounded, tight tracking, optional trailing slot.
export function LargeTitle({ title, subtitle, trailing }) {
  return (
    <div className="pt-2 pb-3">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-round font-bold text-[32px] leading-none tracking-[-0.02em] text-pronto-ink">
          {title}
        </h1>
        {trailing}
      </div>
      {subtitle && (
        <div className="font-sf text-[13.5px] text-pronto-text-2 mt-1.5">{subtitle}</div>
      )}
    </div>
  );
}

// Uppercase eyebrow (e.g. "◇ PRONTO SUGGESTS", "conferma").
export function Eyebrow({ children, className = "" }) {
  return (
    <div className={`font-sf text-[11px] font-bold uppercase tracking-[0.1em] text-pronto-text-3 ${className}`}>
      {children}
    </div>
  );
}

/* ============================ Inset grouped list ============================ */

export function GroupLabel({ children, className = "" }) {
  return (
    <div className={`font-sf text-[12px] font-semibold uppercase tracking-[0.04em] text-pronto-text-3 px-1 mt-5 mb-2 ${className}`}>
      {children}
    </div>
  );
}

export function Group({ children, className = "" }) {
  return (
    <div className={`${GLASS} rounded-[18px] overflow-hidden ${className}`}>{children}</div>
  );
}

// One row in a Group. Icon chip + label + value/chevron.
export function ListRow({ icon, iconClass = "bg-pronto-navy", label, sublabel, value, valueTint, chevron, onClick, children }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 min-h-[48px] text-left border-b-[0.5px] ${SEP} last:border-b-0`}
    >
      {icon && (
        <span className={`w-[26px] h-[26px] rounded-[7px] grid place-items-center flex-shrink-0 text-white ${iconClass}`}>
          {icon}
        </span>
      )}
      <span className="flex-1 min-w-0">
        <span className="block font-sf text-[15px] text-pronto-ink truncate">{label}</span>
        {sublabel && <span className="block font-sf text-[12px] text-pronto-text-2 truncate">{sublabel}</span>}
      </span>
      {children}
      {value != null && (
        <span className={`font-round font-semibold text-[15px] tabular ${valueTint ? "text-pronto-navy" : "text-pronto-ink"}`}>
          {value}
        </span>
      )}
      {chevron && <ChevronRight size={15} className="text-pronto-text-3 ml-1 flex-shrink-0" />}
    </Tag>
  );
}

// "Usa subito" style row: white inset surface with an urgency left-accent.
export function ExpiringRow({ icon, label, meta, accent = "#C0552F", action, className = "" }) {
  return (
    <div
      className={`flex items-center gap-3 rounded-[13px] px-3 py-2.5 bg-[var(--pg-75)] ${className}`}
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      {icon && <span className="text-[18px] leading-none">{icon}</span>}
      <span className="flex-1 min-w-0">
        <span className="block font-sf text-[14px] font-medium text-pronto-ink truncate">{label}</span>
        {meta && <span className="block font-sf text-[12px] text-pronto-text-2 truncate">{meta}</span>}
      </span>
      {action}
    </div>
  );
}

/* ============================ Chat ============================ */

// Asymmetric iMessage-style bubble. from: "user" (accent gradient) | "assistant" (glass).
export function ChatBubble({ from = "assistant", children, className = "" }) {
  const isUser = from === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} ${className}`}>
      <div
        className={`max-w-[80%] px-3 py-2.5 font-sf text-[14px] leading-snug ${
          isUser ? `${ACCENT_GRAD} text-white` : `${GLASS} text-pronto-ink`
        }`}
        style={{ borderRadius: isUser ? "18px 18px 5px 18px" : "18px 18px 18px 5px" }}
      >
        {children}
      </div>
    </div>
  );
}

// Tool-step trace line ("✓ searched recipes · read pantry").
export function ToolStep({ children }) {
  return (
    <div className="font-sf text-[11px] text-pronto-text-3 px-1 py-0.5">{children}</div>
  );
}

// Inline "conferma" approval card with Sì / No.
export function ApprovalCard({ eyebrow = "conferma", statement, onConfirm, onDeny, confirmLabel = "Sì, add", denyLabel = "No" }) {
  return (
    <div className={`${GLASS} rounded-[14px] border-[rgba(63,74,99,0.3)] p-3`}>
      <Eyebrow className="mb-1">{eyebrow}</Eyebrow>
      <div className="font-sf text-[14px] text-pronto-ink mb-3">{statement}</div>
      <div className="flex gap-2">
        <PrimaryButton onClick={onConfirm} className="flex-1 h-9">{confirmLabel}</PrimaryButton>
        <button
          onClick={onDeny}
          className="flex-1 h-9 rounded-[14px] bg-[var(--pg-7)] border border-[var(--p-sep-12)] text-pronto-ink font-sf font-semibold text-[14px]"
        >
          {denyLabel}
        </button>
      </div>
    </div>
  );
}

// Horizontal conversation-tabs strip (recent threads).
export function ConversationTabs({ items, activeId, onSelect, className = "" }) {
  return (
    <div className={`flex gap-2 overflow-x-auto pb-2 border-b ${SEP} ${className}`}>
      {items.map((c) => (
        <Chip key={c.id} tone={c.id === activeId ? "accent" : "rest"} onClick={() => onSelect?.(c.id)} className="whitespace-nowrap flex-shrink-0">
          {c.label}
        </Chip>
      ))}
    </div>
  );
}

/* ============================ Persistent chrome ============================ */

// Bottom tab bar (phone). tabs: [{id,label,icon:Component}]. Frosted, accent-active.
export function TabBar({ tabs, activeId, onSelect }) {
  return (
    <nav className="bg-[var(--pg-6)] backdrop-blur-[24px] border-t border-[var(--pg-border)] px-1.5 pt-2 pb-1">
      <div className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0,1fr))` }}>
        {tabs.map((t) => {
          const on = t.id === activeId;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => onSelect(t.id)}
              className={`flex flex-col items-center gap-0.5 py-1 ${on ? "text-pronto-navy" : "text-pronto-text-4"}`}
            >
              {Icon && <Icon size={20} strokeWidth={on ? 2.4 : 2} />}
              <span className="font-sf text-[9.5px] font-medium">{t.label}</span>
            </button>
          );
        })}
      </div>
      <div className="mx-auto mt-1 h-[5px] w-[120px] rounded-full bg-pronto-ink/[0.26]" />
    </nav>
  );
}

// Desktop left sidebar (same 5 items).
export function Sidebar({ tabs, activeId, onSelect, footer }) {
  return (
    <aside
      className={`${GLASS} w-[220px] rounded-[18px] p-3 flex flex-col gap-1`}
    >
      {tabs.map((t) => {
        const on = t.id === activeId;
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            onClick={() => onSelect(t.id)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-[10px] font-sf text-[14px] font-medium ${
              on ? "bg-pronto-navy text-white" : "text-pronto-navy-deep hover:bg-[rgba(63,74,99,0.08)]"
            }`}
          >
            {Icon && <Icon size={18} />}
            {t.label}
          </button>
        );
      })}
      {footer && <div className="mt-auto pt-3">{footer}</div>}
    </aside>
  );
}

// Persistent mini now-playing bar (every screen except Ask).
export function NowPlayingBar({ art, title, subtitle, room, playing, onToggle, onRoom, onClick }) {
  return (
    <div className={`${GLASS} rounded-[16px] shadow-pronto-bar h-[52px] flex items-center gap-2.5 pl-2 pr-2.5`}>
      <button onClick={onClick} className="flex items-center gap-2.5 flex-1 min-w-0 text-left">
        <span className="w-9 h-9 rounded-[9px] bg-pronto-bag flex-shrink-0 overflow-hidden">{art}</span>
        <span className="flex-1 min-w-0">
          <span className="block font-sf text-[13px] font-semibold text-pronto-ink truncate">{title}</span>
          {subtitle && <span className="block font-sf text-[11px] text-pronto-text-2 truncate">{subtitle}</span>}
        </span>
      </button>
      {room && (
        <button
          onClick={onRoom}
          className="rounded-full px-2.5 py-1 bg-[rgba(63,74,99,0.12)] text-pronto-navy font-sf text-[11px] font-semibold flex-shrink-0"
        >
          {room}
        </button>
      )}
      <button
        onClick={onToggle}
        className={`w-8 h-8 rounded-full ${ACCENT_GRAD} text-white grid place-items-center flex-shrink-0`}
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? "❚❚" : "▶"}
      </button>
    </div>
  );
}

// Floating Ask button (phone, non-Ask screens).
export function AskFAB({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`w-[52px] h-[52px] rounded-full ${ACCENT_GRAD} text-white shadow-pronto-fab grid place-items-center`}
      aria-label="Ask Pronto"
    >
      {children}
    </button>
  );
}

/* ============================ Sheet ============================ */

// iOS slide-up bottom sheet (spring). Respects prefers-reduced-motion via Framer.
export function Sheet({ open, onClose, children }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-x-0 bottom-0 z-[44] flex justify-center pointer-events-none"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 34, stiffness: 340 }}
          >
            <div className="pointer-events-auto w-full max-w-[460px] bg-pronto-page rounded-t-[28px] max-h-[94vh] overflow-hidden flex flex-col font-sf shadow-[0_-8px_40px_-8px_rgba(48,50,66,0.4)]">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
